/**
 * Pregnancy Timeline Page
 *
 * Displays pregnancy events grouped by gestational week,
 * weight gain chart, and upcoming appointments.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, differenceInWeeks, isFuture } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { eventApi, babyApi } from '../services/api';
import { getBabyKey, decryptEventData, decryptBabyProfile } from '../crypto';
import type { Event, EventType } from '../types';

interface TimelineEntry {
  id: string;
  type: EventType;
  timestamp: Date;
  summary: string;
  gestationalWeek: number;
  notes: string | null;
  icon: string;
}

interface WeightEntry {
  gestationalWeek: number;
  weight: number;
  date: Date;
}

const EVENT_ICONS: Record<string, string> = {
  SYMPTOM: '🤢',
  APPOINTMENT: '🏥',
  BLOOD_PRESSURE: '🩺',
  ULTRASOUND: '📷',
  KICK_COUNT: '🦶',
  CONTRACTION: '⏱️',
  WEIGHT: '⚖️',
  MEDICATION: '💊',
  TEMPERATURE: '🌡️',
  JOURNAL: '📝',
};

function getEventSummary(type: EventType, data: Record<string, unknown>): string {
  switch (type) {
    case 'SYMPTOM':
      return `${data.symptomType || 'Symptom'}${data.symptomSeverity ? ` (${data.symptomSeverity})` : ''}`;
    case 'APPOINTMENT': {
      const date = data.nextAppointmentDate
        ? ` | Next: ${format(new Date(data.nextAppointmentDate as string), 'MMM d')}`
        : '';
      return `${data.appointmentType || 'Appointment'}${data.providerName ? ` with ${data.providerName}` : ''}${date}`;
    }
    case 'BLOOD_PRESSURE':
      return `BP ${data.systolic || '?'}/${data.diastolic || '?'}${data.heartRate ? ` | HR ${data.heartRate}` : ''}`;
    case 'ULTRASOUND':
      return `${data.ultrasoundType || 'Ultrasound'}${data.gestationalAgeWeeks ? ` at ${data.gestationalAgeWeeks}w` : ''}`;
    case 'KICK_COUNT':
      return data.kickCount ? `${data.kickCount} kicks` : 'Kick count session';
    case 'CONTRACTION':
      return `Contraction${data.contractionDuration ? ` ${data.contractionDuration}s` : ''}`;
    case 'WEIGHT':
      return `${data.weight}${data.unit || 'kg'}`;
    case 'MEDICATION':
      return `${data.medicationName || 'Medication'}${data.dose ? ` - ${data.dose}` : ''}`;
    case 'TEMPERATURE':
      return `${data.temperature}°${data.tempUnit || 'C'}`;
    case 'JOURNAL':
      return (data.title as string) || 'Journal entry';
    default:
      return type;
  }
}

function calculateGestationalWeek(eventDate: Date, dueDate: Date): number {
  const weeksUntilDue = differenceInWeeks(dueDate, eventDate);
  return Math.max(1, Math.min(40, 40 - weeksUntilDue));
}

export default function PregnancyTimeline() {
  const { babyId } = useParams();
  const [babyProfile, setBabyProfile] = useState<{ name?: string; dueDate?: string; profileType?: string } | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [weights, setWeights] = useState<WeightEntry[]>([]);
  const [decryptionError, setDecryptionError] = useState<string | null>(null);

  // Fetch baby profile
  const { data: babyData } = useQuery({
    queryKey: ['baby', babyId],
    queryFn: async () => {
      if (!babyId) return null;
      const response = await babyApi.getById(babyId);
      return response.data || response;
    },
    enabled: !!babyId,
  });

  // Decrypt baby profile
  useEffect(() => {
    async function loadProfile() {
      if (!babyData?.encryptedData || !babyId) return;
      try {
        const aesKey = await getBabyKey(babyId);
        if (!aesKey) return;
        const decoded = await decryptBabyProfile(babyData.encryptedData, aesKey);
        const d = decoded as unknown as Record<string, unknown>;
        setBabyProfile({
          name: (d.name as string) || '',
          dueDate: d.dueDate as string,
          profileType: d.profileType as string,
        });
        if (d.dueDate) {
          setDueDate(new Date(d.dueDate as string));
        }
      } catch { /* ignore */ }
    }
    loadProfile();
  }, [babyData, babyId]);

  // Fetch all events for the pregnancy
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['pregnancyEvents', babyId],
    queryFn: async () => {
      if (!babyId) return { success: true, data: { events: [] } };
      const response = await eventApi.list(babyId, { limit: '1000' });
      return response;
    },
    enabled: !!babyId,
  });

  // Decrypt events
  useEffect(() => {
    async function decryptEvents() {
      if (!eventsData?.data?.events || !babyId || !dueDate) return;
      try {
        const aesKey = await getBabyKey(babyId);
        if (!aesKey) { setDecryptionError('Could not find encryption key'); return; }

        const all: TimelineEntry[] = [];
        const wts: WeightEntry[] = [];

        for (const event of eventsData.data.events as Event[]) {
          try {
            const data = await decryptEventData(event.encryptedData, aesKey);
            const timestamp = new Date(event.timestamp);
            const gw = calculateGestationalWeek(timestamp, dueDate);

            // Weight entries for chart
            if (event.type === 'WEIGHT' && data.weight) {
              wts.push({ gestationalWeek: gw, weight: Number(data.weight), date: timestamp });
            }

            all.push({
              id: event.id,
              type: event.type as EventType,
              timestamp,
              summary: getEventSummary(event.type as EventType, data as Record<string, unknown>),
              gestationalWeek: gw,
              notes: null, // notes are decrypted separately
              icon: EVENT_ICONS[event.type] || '📋',
            });
          } catch { /* skip events that can't be decrypted */ }
        }

        all.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        wts.sort((a, b) => a.gestationalWeek - b.gestationalWeek);

        setEntries(all);
        setWeights(wts);
        setDecryptionError(null);
      } catch (err) {
        console.error('Failed to decrypt events:', err);
        setDecryptionError('Failed to decrypt events');
      }
    }
    decryptEvents();
  }, [eventsData, babyId, dueDate]);

  // Group entries by gestational week
  const groupedEntries = useMemo(() => {
    const groups = new Map<number, TimelineEntry[]>();
    for (const entry of entries) {
      const existing = groups.get(entry.gestationalWeek) || [];
      existing.push(entry);
      groups.set(entry.gestationalWeek, existing);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => b - a);
  }, [entries]);

  // Upcoming appointments (future-dated events or nextAppointmentDate)
  const upcomingAppointments = useMemo(() => {
    return entries
      .filter(e => e.type === 'APPOINTMENT' && isFuture(e.timestamp))
      .slice(0, 5);
  }, [entries]);

  // Baseline weight (first entry)
  const baselineWeight = weights.length > 0 ? weights[0].weight : null;

  if (babyProfile && babyProfile.profileType !== 'PREGNANCY') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <span className="text-5xl">👶</span>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-4">
          This is a baby profile
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          The pregnancy timeline is only available for pregnancy profiles.
        </p>
        <Link
          to={`/app/baby/${babyId}/health`}
          className="btn-primary mt-4 inline-block"
        >
          Go to Health Metrics
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/app/dashboard`}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2 text-sm inline-block"
        >
          &larr; Back to Dashboard
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Pregnancy Timeline
        </h1>
        {babyProfile?.name && (
          <p className="text-gray-600 dark:text-gray-400">{babyProfile.name}</p>
        )}
      </div>

      {/* Decryption error */}
      {decryptionError && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          {decryptionError}
        </div>
      )}

      {/* Weight Gain Chart */}
      {weights.length >= 2 && (
        <div className="card p-4 mb-6">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
            Weight Gain
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={weights} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis
                dataKey="gestationalWeek"
                label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                domain={['auto', 'auto']}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                labelFormatter={(week) => `Week ${week}`}
                formatter={(value) => [`${value} kg`, 'Weight']}
              />
              {baselineWeight && (
                <ReferenceLine
                  y={baselineWeight}
                  stroke="#94a3b8"
                  strokeDasharray="5 5"
                  label={{ value: 'Pre-pregnancy', position: 'right', fontSize: 11 }}
                />
              )}
              <Line
                type="monotone"
                dataKey="weight"
                stroke="#ec4899"
                strokeWidth={2}
                dot={{ fill: '#ec4899', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Weekly Timeline */}
      {groupedEntries.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-4xl">📋</span>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            No events logged yet
          </p>
          <Link
            to={`/app/baby/${babyId}/event/new`}
            className="btn-primary mt-4 inline-block"
          >
            Log First Event
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {groupedEntries.map(([week, weekEntries]) => (
            <div key={week}>
              {/* Week header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 px-3 py-1 rounded-full font-semibold text-sm">
                  Week {week}
                </div>
                <div className="h-px flex-1 bg-pink-200 dark:bg-pink-800" />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {weekEntries.length} event{weekEntries.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Events */}
              <div className="space-y-2">
                {weekEntries.map(entry => (
                  <Link
                    key={entry.id}
                    to={`/app/baby/${babyId}/event/${entry.id}`}
                    className="block card p-3 border bg-white dark:bg-gray-800 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{entry.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {entry.summary}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {format(entry.timestamp, 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upcoming Appointments */}
      {upcomingAppointments.length > 0 && (
        <div className="card p-4 mt-6 bg-pink-50 dark:bg-pink-900/10 border-pink-200 dark:border-pink-800">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-3">
            Upcoming Appointments
          </h2>
          <div className="space-y-2">
            {upcomingAppointments.map(apt => (
              <div
                key={apt.id}
                className="flex items-center gap-3 bg-white dark:bg-gray-800 p-3 rounded-lg"
              >
                <span className="text-xl">🏥</span>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {apt.summary}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {format(apt.timestamp, 'EEEE, MMM d, yyyy h:mm a')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
